import React, { useCallback, useMemo } from 'react'
import QRCode from 'react-qr-code'
import { shallowEqual, useSelector } from 'react-redux'
import block from 'bem-cn'
import { useFormik } from 'formik'

import { PaymentTypes, PaymentTypesType } from 'services/api/PaymentApi/types/common'
import { ISystemListItem } from 'services/api/PaymentApi/types/types'

import { ReactComponent as ArrowSvg } from 'shared/img/payment/arrow.svg'

// import { ReactComponent as LockSvg } from 'shared/img/lock-svg.svg'
import { defaultCurrency } from 'shared/constants'
import { paymentNameSeparator } from 'shared/constants/payment'
import { LocalePaymentKeysType } from 'shared/locale/en-US'
import { ExtraSchema, SelectorValue } from 'shared/models/Payments'
import { IUser } from 'shared/models/User'
// import { getFormattedValue } from 'shared/utils/cpf'
import {
  getFormattedExtraFieldsForTransfer,
  getInitialValuesForPaymentFields,
  getValidateForPaymentFields,
} from 'shared/utils/payments'

import { selectLocaleDict } from 'features/locale/selectors'
import { selectGetCashiersList } from 'features/payment/selectors'
import { selectLanguage } from 'features/userSettings/selectors'

import { PaymentFieldGroup } from 'pages/ProfilePage/components/PaymentFieldGroup'
import { QrCodeTimer } from 'pages/ProfilePage/components/QrCodeTimer'
import { TemplateAmount } from 'pages/ProfilePage/components/TemplateAmount'

// import { phoneMaskUtilsWithoutCode } from 'pages/Auth/SignUp/data'
import CopyButtonHoc from 'components/HOC/CopyButtonHoc/CopyButtonHoc'

import Loader from 'components/Loader/Loader'
import { Form, FormInput } from 'components/UIKit'
import Button, { ButtonSizes } from 'components/UIKit/Button/Button'

import { useConvertAmount, useCreateTransfer } from '../../hooks'
import { PaymentScreens } from '../PaymentScreens'
import { RequisiteBlock } from '../RequisiteBlock/RequisiteBlock'

import './CurrentPayment.scss'

interface IPaymentDetails {
  user: IUser | null
  systemList: ISystemListItem[]
  selectedSystem: string
  paymentType: PaymentTypesType
  changeSystem: (system: string) => void
}

const b = block('current-payment')

export const CurrentPayment: React.FC<IPaymentDetails> = props => {
  const { systemList, user, selectedSystem, paymentType, changeSystem } = props
  const locale = useSelector(selectLocaleDict, shallowEqual).payment
  const lang = useSelector(selectLanguage, shallowEqual)
  const cashiersList = useSelector(selectGetCashiersList, shallowEqual)

  const onCloseCurrentPayment = useCallback(() => {
    changeSystem('')
  }, [changeSystem])

  const { currentSlug, additionSlug } = useMemo(
    () => ({
      currentSlug: selectedSystem.split(paymentNameSeparator)[0],
      additionSlug: selectedSystem.split(paymentNameSeparator)[1],
    }),
    [selectedSystem],
  )

  // const cryptoDescription = useMemo(
  //   () => getCryptoWithdrawNotification(lang, additionSlug) ?? undefined,
  //   [additionSlug, lang],
  // )

  const {
    isLoading,
    isConvertAmountLoading,
    qrCodeData,
    convertedAmountData,
    files,
    setFiles,
    handleClear,
    createTransfer,
  } = useCreateTransfer({
    type: paymentType,
    locale,
    selectedSystem,
    onCloseCurrentPayment,
  })

  const currentPaymentSystemData = useMemo(
    () => systemList.find(item => item.slug === currentSlug),
    [currentSlug, systemList],
  )

  const onCreateTransfer = useCallback(
    (values: Record<string, string | number>) => {
      if (!currentPaymentSystemData || !user) return

      const { name: system, method, fields, slug } = currentPaymentSystemData

      const { currentAmount, extra } = getFormattedExtraFieldsForTransfer({
        values,
        fields,
        estimatedAmount: convertedAmountData?.estimatedAmount,
      })

      const payload = {
        id: slug,
        amount: String(currentAmount),
        system,
        method,
        extra,
      }

      createTransfer(payload)
    },
    [currentPaymentSystemData, user, convertedAmountData?.estimatedAmount, createTransfer],
  )

  const userInfo = useMemo(
    () => ({
      fullName: user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      // phone: getFormattedValue(user?.mobileNumber, phoneMaskUtilsWithoutCode.mask),
      additionSlug,
    }),
    [additionSlug, user?.email, user?.firstName, user?.lastName],
  )

  const initialValues = useMemo(
    () => getInitialValuesForPaymentFields({ systemData: currentPaymentSystemData, userInfo }),
    [currentPaymentSystemData, userInfo],
  )

  const validate = useMemo(
    () => (values: Record<string, string | number>) =>
      getValidateForPaymentFields({ values, systemData: currentPaymentSystemData }),
    [currentPaymentSystemData],
  )

  const formik = useFormik({
    initialValues,
    onSubmit: onCreateTransfer,
    validate,
    enableReinitialize: true,
  })

  useConvertAmount({
    amount: formik.values.amount,
    currencyFrom: user?.currency,
    currencyTo: additionSlug,
  })

  const limits = useMemo(
    () =>
      currentPaymentSystemData
        ? `${locale.min} ${currentPaymentSystemData?.min} ${user?.currency} / 
      ${locale.max} ${currentPaymentSystemData?.max} ${user?.currency}`
        : null,
    [locale, currentPaymentSystemData, user],
  )

  const isDisabledButton = useMemo(
    () =>
      !formik.isValid ||
      isLoading ||
      (paymentType === PaymentTypes.DEPOSIT &&
        currentPaymentSystemData?.depositImagesEnabled &&
        currentPaymentSystemData.depositImagesRequired &&
        !files.length),
    [
      currentPaymentSystemData?.depositImagesEnabled,
      currentPaymentSystemData?.depositImagesRequired,
      files.length,
      paymentType,
      isLoading,
      formik.isValid,
    ],
  )

  const estimatedAmountBlock = useMemo(() => {
    if (!additionSlug) {
      return null
    }
    if (isConvertAmountLoading) {
      return (
        <span className={b('estimate-amount')}>
          <Loader loading />
        </span>
      )
    }
    return (
      <span className={b('estimate-amount')}>{`≈ ${
        formik.values.amount ? convertedAmountData?.estimatedAmount ?? 0 : 0
      } ${additionSlug}`}</span>
    )
  }, [additionSlug, convertedAmountData?.estimatedAmount, isConvertAmountLoading, formik.values.amount])

  const adornments = useMemo(() => {
    // const lockBlock = <div className={b('lock-block')}>{/* <LockSvg className={b('lock-icon')} /> */}</div>

    const userCurrency = <div className={b('user-currency')}>{user?.currency}</div>

    return { /** lockBlock, */ userCurrency }
  }, [user?.currency])

  const fieldsRender = useCallback(
    (fields?: ExtraSchema[], nestedCount = 0): React.ReactNode =>
      fields?.map(field => {
        const isArray = Array.isArray(field)
        let title = isArray
          ? field[0].name.split(paymentNameSeparator)[nestedCount]
          : field?.name.split(paymentNameSeparator)[1]
        if (isArray) {
          return (
            <div key={`block-${title}`} className={b('block')}>
              <div className={b('block-title')}>{locale[title as LocalePaymentKeysType] ?? title}</div>
              {fieldsRender(field, nestedCount + 1)}
            </div>
          )
        }

        title = field?.name.includes(paymentNameSeparator)
          ? field?.name.split(paymentNameSeparator)[nestedCount]
          : field?.name

        let convertedValues: SelectorValue[] = []

        if (field.fieldType === 'selector') {
          if (field?.values.length) {
            convertedValues = field.values
          } else if (field.name === 'cashier_id') {
            convertedValues = cashiersList.map(it => ({
              title: it.username,
              value: it.id.toString(),
            }))
          }
        }

        return (
          <PaymentFieldGroup
            key={`field-${title}`}
            title={title}
            locale={locale}
            field={{ ...field, ...(field.fieldType === 'selector' ? { values: convertedValues } : {}) }}
            touched={formik.touched}
            errors={formik.errors}
            endAdornment={field.fieldType === 'input' && field.endAdornmentType ? adornments : undefined}
            value={formik.values[field.name]}
            onBlur={formik.handleBlur}
            handleChange={formik.handleChange}
            setFieldValue={formik.setFieldValue}
          />
        )
      }),
    [
      adornments,
      formik.errors,
      formik.handleBlur,
      formik.handleChange,
      locale,
      formik.setFieldValue,
      formik.touched,
      formik.values,
      cashiersList,
    ],
  )

  const renderFieldsComponent = useMemo(
    () => fieldsRender(currentPaymentSystemData?.fields),
    [fieldsRender, currentPaymentSystemData?.fields],
  )

  return (
    <div className={b()}>
      {qrCodeData ? (
        <div className={b('qrcode-container')}>
          <QRCode value={qrCodeData.url} className={b('qrcode')} />
          <div className={b('qrcode-text')}>
            {locale.payQrCode}
            <QrCodeTimer
              className={b('qrcode-time')}
              expirationDate={qrCodeData.expirationDate}
              handleClear={handleClear}
            />
          </div>
          <div className={b('code-wrapper')}>
            <div className={b('code-text')}>{qrCodeData.url}</div>
            <div className={b('code-copy-wrapper')}>
              <CopyButtonHoc text={qrCodeData.url}>{locale.copy}</CopyButtonHoc>
            </div>
          </div>
          {handleClear && (
            <div className={b('qrcode-button')}>
              <Button onClick={handleClear} text={locale.cancelPayment} />
            </div>
          )}
        </div>
      ) : (
        <Form formik={formik}>
          <div className={b('header')}>
            <span className={b('header-arrow')} onClick={onCloseCurrentPayment}>
              <ArrowSvg className={b('header-arrow-icon')} />
            </span>
            <span className={b('header-title')}>{locale[paymentType]}</span>
          </div>

          {/* {cryptoDescription && ( */}
          {/*  <div className={b('desc')}> */}
          {/*    <DepositInfo locale={locale} isCrypto description={cryptoDescription} /> */}
          {/*  </div> */}
          {/* )} */}

          {!!currentPaymentSystemData?.requisites.length && (
            <div className={b('group')}>
              {currentPaymentSystemData?.requisites.map((requisite, index) => (
                <RequisiteBlock key={`${index}-${requisite.value}`} requisite={requisite} />
              ))}
            </div>
          )}
          {paymentType === 'payout' && (
            <div className={b('balance')}>{`${locale.yourBalance}: ${user?.balance} ${user?.currency}`}</div>
          )}
          <div className={b('group')}>
            <label className={b('group-label')}>{locale.amount}</label>
            <div
              className={b('group-input', {
                error: Boolean(formik.touched.amount && formik.errors.amount),
                correct: Boolean(formik.touched.amount && !formik.errors.amount),
              })}>
              <FormInput
                name="amount"
                type="number"
                suffix={additionSlug ? adornments.userCurrency : null}
                decimals={2}
              />
              {estimatedAmountBlock}
            </div>
          </div>
          <TemplateAmount
            paymentType={paymentType}
            changeValue={value => formik.setFieldValue('amount', value)}
            currency={user?.currency || defaultCurrency}
            balance={user?.balance}
          />
          <div className={b('group', { limits: true })}>
            <label>{limits}</label>
          </div>
          {renderFieldsComponent}

          {paymentType === PaymentTypes.DEPOSIT && !!currentPaymentSystemData && (
            <PaymentScreens
              enabled={currentPaymentSystemData.depositImagesEnabled}
              maxFiles={currentPaymentSystemData.depositImagesMaxCount}
              required={currentPaymentSystemData.depositImagesRequired}
              onChange={setFiles}
              files={files}
            />
          )}

          {/* <DescriptionBlock paymentKey={`${paymentType}_${currentPaymentSystemData?.method}`} /> */}
          <div className={b('buttons-wrapper')}>
            {/* <div className={b('button-wrapper')}>
              <Button
                text={locale.back}
                callback={onCloseCurrentPayment}
                uppercase
              />
            </div> */}
            <div className={b('button-wrapper')}>
              <Button
                text={paymentType === PaymentTypes.DEPOSIT ? locale.deposit : locale.cashOut}
                type="submit"
                disabled={isDisabledButton}
                loading={isLoading}
                fullWidth
                size={ButtonSizes.LARGE}
              />
            </div>
          </div>
        </Form>
      )}
    </div>
  )
}
